import { useEffect, useState, type FormEvent } from 'react';
import { Alert } from '@/components/alert';
import { Button } from '@/components/button';
import { TextField } from '@/components/text-field';
import type { Copy } from '@/lib/messages';

interface EmailStepProps {
  copy: Copy;
  pending: boolean;
  /** Form-level error (network, throttle, …) — rendered as the top alert. */
  formError: string | null;
  /** Field-level error (a malformed address) — rendered on the email field. */
  fieldError: string | null;
  onSubmit: (email: string) => void;
  /** Overrides the button copy where "send code" is not what this step means. */
  submitLabel?: string;
  /**
   * True once a conditional WebAuthn request is listening on this field. It
   * changes nothing visible — the browser draws the passkey row in its own
   * autofill menu — but it is why the input advertises `webauthn`.
   */
  passkeyAutofill?: boolean;
}

export function EmailStep({
  copy,
  pending,
  formError,
  fieldError,
  onSubmit,
  submitLabel,
  passkeyAutofill = false,
}: EmailStepProps) {
  const [email, setEmail] = useState('');
  // Whether THIS form is the one that submitted, so the spinner rides the send
  // button only when the send button was pressed — the alternatives beside it
  // (passkey, social) share the same global `pending`, and a single `loading`
  // used to make "Send code" spin for the whole of, say, a passkey ceremony,
  // which reads as the email path having failed. Mirrors the consent step's
  // per-control pattern. Reset once the request settles so a later click on a
  // sibling control does not inherit this one's spinner.
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    if (!pending) setSubmitted(false);
  }, [pending]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = email.trim();
    if (trimmed) {
      setSubmitted(true);
      onSubmit(trimmed);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      {formError ? <Alert tone="error">{formError}</Alert> : null}
      <TextField
        id="email"
        label={copy.emailLabel}
        type="email"
        error={fieldError}
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        // `webauthn` is what puts a saved passkey into the browser's own
        // autofill menu for this field, so a returning user signs in by
        // picking it — no button, no typing, no email at all.
        autoComplete={passkeyAutofill ? 'username webauthn' : 'username'}
        inputMode="email"
        // Phone keyboards otherwise capitalise the first letter of an address.
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder={copy.emailPlaceholder}
        autoFocus
        required
      />
      {/* Disabled during ANY pending (no double-post across methods), but the
          spinner shows only for this form's own submit. */}
      <Button
        type="submit"
        loading={pending && submitted}
        disabled={!email.trim() || pending}
      >
        {submitLabel ?? copy.emailSubmit}
      </Button>
    </form>
  );
}
